import React, { useContext, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import {
  NavigationContext,
  UserContext,
  UserSettingsContext,
} from '@eas/common-web';
import { useColumns as useDeadlineColumns } from '@modules/deadlines/deadline-columns';
import { useColumns as useUnprocessedColumns } from '@modules/document-delivered-unprocessed/unprocessed-columns';
import { useColumns as useAllEsFilesColumns } from '@modules/es-file-all/all-es-files-columns';
import { useColumns as useCourtFilesJudgeOfficeColumns } from '@modules/es-file-court/judge-office/court-file-judge-office-columns';
import { useColumns as useTaskColumns } from '@modules/task/task-columns';
import { Me, UserRole } from '@models';
import { Role } from '@enums';
import { DashboardButton } from '../../button';
import { DashboardCard } from '../../card';
import { useUserOverviewStatistics } from '../../dashboard-hook';
import { getCurrentUserJudge } from '../utils';
import { useCourtFileCounts } from './use-court-file-counts';
import {
  fetchAssistantUserRole,
  useUserOverviewItemsByRoles,
} from './user-overview-by-roles';

export function UserOverviewStatistics() {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const [assistantRole, setAssistantRole] = useState<UserRole>();
  const substitutedUserId =
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id;

  useEffect(() => {
    let active = true;
    setAssistantRole(undefined);
    if (user?.userRoles?.some((role) => role.role === Role.ASSISTANT)) {
      fetchAssistantUserRole(user).then((role) => {
        if (active) setAssistantRole(role);
      });
    }
    return () => {
      active = false;
    };
  }, [user?.id, substitutedUserId, user?.userRoles]);

  const isJudge =
    user?.userRoles?.some((role) => role.role === Role.JUDGE) ?? false;
  const { judgeCount, assistantCount, assistantJudges } = useCourtFileCounts(
    isJudge ? getCurrentUserJudge(user).id : undefined,
    user?.userRoles?.some((role) => role.role === Role.ASSISTANT)
      ? assistantRole?.id
      : undefined
  );

  const { navigate } = useContext(NavigationContext);
  const { getTableSettings, setTableSettings } =
    useContext(UserSettingsContext);

  const statistics = useUserOverviewStatistics(isJudge);

  const taskColumns = useTaskColumns();
  const allEsFilesColumns = useAllEsFilesColumns();
  const unprocessedDocumentsColumns = useUnprocessedColumns();
  const deadlineColumns = useDeadlineColumns();
  const courtFilesJudgeOfficeColumns = useCourtFilesJudgeOfficeColumns();

  const items = useUserOverviewItemsByRoles({
    user,
    statistics,
    taskColumns,
    allEsFilesColumns,
    unprocessedDocumentsColumns,
    deadlineColumns,
    courtFilesJudgeOfficeColumns,
    judgeFileCount: judgeCount,
    assistantFileCount: assistantCount,
    assistantJudges,
    assistantRole,
    getTableSettings,
    setTableSettings,
    navigate,
  });

  return (
    <Grid item xs={12}>
      <DashboardCard>
        {items.map((item) => (
          <DashboardButton
            key={item.key}
            label={item.label}
            value={item.value}
            onClick={item.onClick}
            showAction={true}
          />
        ))}
      </DashboardCard>
    </Grid>
  );
}
